import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TrendingUp, Clock, Heart, MessageCircle, Eye, ArrowUpDown, Filter, Loader2 } from 'lucide-react';
import Post from './Post';
import { 
  fetchPostsWithPictures, 
  loadMorePosts, 
  likePost, 
  unlikePost,
  bullPost,
  bearPost,
  removeBearVote, 
  removeBullVote,
  fetchPostsWithFilters 
} from '../../redux/posts/postActions';
import { setPostFilters, resetPostsForNewQuery } from '../../redux/posts/postReducer';
import { createComment, likeComment, unlikeComment } from '../../redux/comments/commentActions';
import { incrementTrendingViewThunk } from '../../redux/trending/trendingActionThunks';
import { fetchMySavedPostIdsThunk } from '../../redux/savedPosts/savedPostsActions';
import { useBulkProfilePictures } from '../../redux/user/profilePictures/useBulkProfilePictures';
// Matches Post.jsx's real card shape (rounded-2xl card, w-10/12 avatar,
// name+timestamp header, a couple of content lines, footer action row)
// so the swap to real posts doesn't jump.
const PostCardSkeleton = () => (
  <div className="relative w-full bg-gradient-to-br from-gray-900/95 to-gray-800/95 border border-gray-700/50 rounded-2xl p-4 sm:p-5 min-h-[260px] mb-4 sm:mb-6 animate-pulse">
    <div className="flex items-start gap-3 mb-4">
      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gray-700/60 flex-shrink-0" />
      <div className="flex-1 space-y-2 pt-1">
        <div className="h-4 bg-gray-700/60 rounded w-32" />
        <div className="h-3 bg-gray-700/40 rounded w-20" />
      </div>
      <div className="h-6 bg-gray-700/50 rounded-full w-16" />
    </div>
    <div className="space-y-2 mb-4">
      <div className="h-4 bg-gray-700/50 rounded w-full" />
      <div className="h-4 bg-gray-700/50 rounded w-5/6" />
      <div className="h-4 bg-gray-700/50 rounded w-2/3" />
    </div>
    <div className="h-20 bg-gray-800/60 rounded-xl border border-gray-700/40 mb-4" />
    <div className="flex items-center gap-4 pt-2 border-t border-gray-700/40">
      <div className="h-5 bg-gray-700/50 rounded w-12" />
      <div className="h-5 bg-gray-700/50 rounded w-12" />
      <div className="h-5 bg-gray-700/50 rounded w-12" />
    </div>
  </div>
);

const SORT_OPTION_META = [
  { id: 'latest', icon: Clock },
  { id: 'trending', icon: TrendingUp },
  { id: 'popular', icon: Heart },
  { id: 'discussed', icon: MessageCircle },
  { id: 'viewed', icon: Eye },
  { id: 'bull_sentiment', icon: TrendingUp },
  { id: 'bear_sentiment', icon: TrendingUp, iconRotation: 'rotate-180' }
];

const TIME_FILTER_IDS = ['all', 'today', 'week', 'month', 'year'];

const Posts = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { 
    posts = [], 
    hasMore, 
    loading, 
    error, 
    currentFilters,
    isFiltered,
    filterLoading 
  } = useSelector((state) => state.posts || {});
  const trendingItems = useSelector(state => state.trending?.trendingItems || []);
  const currentUser = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  
  const [openCommentBoxPostId, setOpenCommentBoxPostId] = useState(null);
  const [optimisticComments, setOptimisticComments] = useState({});
  const [optimisticLikes, setOptimisticLikes] = useState({});
  const [optimisticBullBear, setOptimisticBullBear] = useState({});
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showTimeFilter, setShowTimeFilter] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Local state for UI responsiveness
  const [localSortBy, setLocalSortBy] = useState(currentFilters?.sort_by || 'latest');
  const [localTimeFilter, setLocalTimeFilter] = useState(currentFilters?.period || 'all');
  
  const viewedPostIds = useRef(new Set());
  const dropdownRef = useRef(null);
  const timeFilterRef = useRef(null);
  const lastFiltersRef = useRef(null);

  // Extract all user IDs that need profile pictures - BULK FETCHING OPTIMIZATION
  const userIds = useMemo(() => {
    const ids = new Set();
    
    posts.forEach(post => {
      // Add post author ID
      const postAuthorId = post.user_id || post.author_id || post.user?.id;
      if (postAuthorId) ids.add(postAuthorId);
      
      // Add comment author IDs
      if (post.comments) {
        post.comments.forEach(comment => {
          const commentAuthorId = comment.user_id || comment.author_id || comment.user?.id;
          if (commentAuthorId) ids.add(commentAuthorId);
        });
      }
    });
    
    return Array.from(ids);
  }, [posts]);

  // Bulk fetch profile pictures for all users - THIS PREVENTS THE API SPAM
  useBulkProfilePictures(userIds);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
      if (timeFilterRef.current && !timeFilterRef.current.contains(event.target)) {
        setShowTimeFilter(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initialize posts on mount
  useEffect(() => {
    if (!isInitialized) {
      // Only fetch if no posts or not filtered
      if (posts.length === 0 || !isFiltered) {
        dispatch(fetchPostsWithPictures());
      }
      // Once per feed mount, not per-post -- each Post.jsx reads the result
      // via its own useSelector(state => state.savedPosts...) rather than
      // every post firing its own "is this saved" round trip.
      dispatch(fetchMySavedPostIdsThunk());
      setIsInitialized(true);
    }

    return () => {
      // Save vote state to localStorage on cleanup
      if (posts && posts.length > 0) {
        const voteState = {};
        posts.forEach(post => {
          if (post.has_bull_vote || post.has_bear_vote) {
            voteState[post.id] = {
              bull: post.has_bull_vote || false,
              bear: post.has_bear_vote || false
            };
          }
        });
        localStorage.setItem('userVotes', JSON.stringify(voteState));
      }
    };
  }, [dispatch, posts, isFiltered, isInitialized]);

  // Load stored votes when posts are loaded
  useEffect(() => {
    if (posts && posts.length > 0) {
      try {
        const storedVotes = localStorage.getItem('userVotes');
        if (storedVotes) {
          const voteState = JSON.parse(storedVotes);
          const newOptimisticVotes = {};
          
          posts.forEach(post => {
            if (voteState[post.id]) {
              newOptimisticVotes[post.id] = voteState[post.id];
            }
          });
          
          if (Object.keys(newOptimisticVotes).length > 0) {
            setOptimisticBullBear(newOptimisticVotes);
          }
        }
      } catch (err) {
        console.warn('Error loading stored votes:', err);
      }
    }
  }, [posts]);

  // Sync local state with Redux filters
  useEffect(() => {
    if (currentFilters) {
      setLocalSortBy(currentFilters.sort_by || 'latest');
      setLocalTimeFilter(currentFilters.period || 'all');
    }
  }, [currentFilters]);

  // Handle post viewed callback
  const handlePostViewed = useCallback(
    (postId) => {
      if (!viewedPostIds.current.has(postId)) {
        dispatch(incrementTrendingViewThunk(postId));
        viewedPostIds.current.add(postId);
      }
    },
    [dispatch]
  );

  // Debounced scroll handler for infinite scroll
  const debounce = useCallback((func, wait) => {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
  }, []);

  const handleScroll = useCallback(() => {
    if (
      window.innerHeight + document.documentElement.scrollTop >= 
      document.documentElement.offsetHeight - 100 && 
      hasMore && 
      !loading && 
      !filterLoading
    ) {
      dispatch(loadMorePosts());
    }
  }, [dispatch, hasMore, loading, filterLoading]);

  useEffect(() => {
    const debouncedHandleScroll = debounce(handleScroll, 300);
    window.addEventListener('scroll', debouncedHandleScroll);
    return () => {
      window.removeEventListener('scroll', debouncedHandleScroll);
    };
  }, [handleScroll, debounce]);

  // Handle sort change
  const handleSortChange = useCallback((newSortBy) => {
    if (newSortBy === localSortBy) return;
    
    setLocalSortBy(newSortBy);
    setShowSortDropdown(false);
    
    // Update Redux state and trigger new fetch
    dispatch(setPostFilters({
      sort_by: newSortBy,
      period: localTimeFilter,
      limit: 10
    }));
    
    dispatch(resetPostsForNewQuery());
    dispatch(fetchPostsWithFilters({
      sort_by: newSortBy,
      period: localTimeFilter,
      skip: 0,
      limit: 10,
      replace: true
    }));
  }, [dispatch, localSortBy, localTimeFilter]);

  // Handle time filter change
  const handleTimeFilterChange = useCallback((newTimeFilter) => {
    if (newTimeFilter === localTimeFilter) return;
    
    setLocalTimeFilter(newTimeFilter);
    setShowTimeFilter(false);
    
    // Update Redux state and trigger new fetch
    dispatch(setPostFilters({
      sort_by: localSortBy,
      period: newTimeFilter,
      limit: 10
    }));
    
    dispatch(resetPostsForNewQuery());
    dispatch(fetchPostsWithFilters({
      sort_by: localSortBy,
      period: newTimeFilter,
      skip: 0,
      limit: 10,
      replace: true
    }));
  }, [dispatch, localSortBy, localTimeFilter]);

  // Toggle comment box
  const toggleCommentBox = useCallback((postId) => {
    setOpenCommentBoxPostId(openCommentBoxPostId === postId ? null : postId);
  }, [openCommentBoxPostId]);

  // Handle like toggle
  // Was fire-and-forget (dispatch(...) with no return) -- Post.jsx's
  // `await onLikeToggle?.(...)` resolved immediately regardless of the
  // real request's outcome, so a failed like/unlike silently left the
  // optimistic flip permanently wrong with no revert and no error. Now
  // async + .unwrap()'d so a genuine rejection reverts the optimistic
  // state and propagates to the caller.
  const handleLikeToggle = useCallback(async (postId, isLiked) => {
    setOptimisticLikes(prev => ({
      ...prev,
      [postId]: !isLiked
    }));

    try {
      if (isLiked) {
        await dispatch(unlikePost({ postId, token })).unwrap();
      } else {
        await dispatch(likePost({ postId, token })).unwrap();
      }
    } catch (err) {
      setOptimisticLikes(prev => ({
        ...prev,
        [postId]: isLiked
      }));
      throw err;
    }
  }, [dispatch, token]);

  // Handle bull vote
  // Same fire-and-forget bug as handleLikeToggle above: Post.jsx's own
  // try/catch revert-on-error (handleBullVote there) was structurally
  // dead code, since this function never returned a promise that could
  // reject. Now async + .unwrap()'d, and the optimistic bull/bear state
  // (plus its localStorage mirror) is reverted to its exact pre-click
  // value on a genuine failure.
  const handleBullVote = useCallback(async (postId, hasBullVote) => {
    const previousVoteState = optimisticBullBear[postId] ?? null;
    const storedVotes = JSON.parse(localStorage.getItem('userVotes') || '{}');

    const applyVoteState = (voteState) => {
      setOptimisticBullBear(prev => ({ ...prev, [postId]: voteState }));
      storedVotes[postId] = voteState;
      localStorage.setItem('userVotes', JSON.stringify(storedVotes));
    };

    const revertVoteState = () => {
      setOptimisticBullBear(prev => {
        const next = { ...prev };
        if (previousVoteState) {
          next[postId] = previousVoteState;
        } else {
          delete next[postId];
        }
        return next;
      });
      if (previousVoteState) {
        storedVotes[postId] = previousVoteState;
      } else {
        delete storedVotes[postId];
      }
      localStorage.setItem('userVotes', JSON.stringify(storedVotes));
    };

    try {
      if (hasBullVote) {
        applyVoteState({ bull: false, bear: false });
        await dispatch(removeBullVote({ postId, token })).unwrap();
      } else {
        applyVoteState({ bull: true, bear: false });
        await dispatch(bullPost({ postId, token })).unwrap();
      }
    } catch (err) {
      revertVoteState();
      throw err;
    }
  }, [dispatch, token, optimisticBullBear]);

  // Handle bear vote (mirrors handleBullVote above)
  const handleBearVote = useCallback(async (postId, hasBearVote) => {
    const previousVoteState = optimisticBullBear[postId] ?? null;
    const storedVotes = JSON.parse(localStorage.getItem('userVotes') || '{}');

    const applyVoteState = (voteState) => {
      setOptimisticBullBear(prev => ({ ...prev, [postId]: voteState }));
      storedVotes[postId] = voteState;
      localStorage.setItem('userVotes', JSON.stringify(storedVotes));
    };

    const revertVoteState = () => {
      setOptimisticBullBear(prev => {
        const next = { ...prev };
        if (previousVoteState) {
          next[postId] = previousVoteState;
        } else {
          delete next[postId];
        }
        return next;
      });
      if (previousVoteState) {
        storedVotes[postId] = previousVoteState;
      } else {
        delete storedVotes[postId];
      }
      localStorage.setItem('userVotes', JSON.stringify(storedVotes));
    };

    try {
      if (hasBearVote) {
        applyVoteState({ bull: false, bear: false });
        await dispatch(removeBearVote({ postId, token })).unwrap();
      } else {
        applyVoteState({ bull: false, bear: true });
        await dispatch(bearPost({ postId, token })).unwrap();
      }
    } catch (err) {
      revertVoteState();
      throw err;
    }
  }, [dispatch, token, optimisticBullBear]);

  // Handle comment submit
  const handleCommentSubmit = useCallback((postId, comment, parentCommentId = null) => {
    const tempId = `temp-${Date.now()}`;
    const optimisticComment = {
      id: tempId,
      content: comment,
      post_id: postId,
      parent_comment_id: parentCommentId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      user: currentUser,
      like_count: 0,
      is_liked: false,
      is_temporary: true
    };

    setOptimisticComments(prev => ({
      ...prev,
      [postId]: [...(prev[postId] || []), optimisticComment]
    }));

    dispatch(createComment({ 
      postId, 
      content: comment, 
      parent_comment_id: parentCommentId 
    }));
  }, [dispatch, currentUser]);

  // Handle comment like toggle
  const handleCommentLikeToggle = useCallback((commentId, postId, isLiked) => {
    setOptimisticLikes(prev => ({
      ...prev,
      [`comment-${commentId}`]: !isLiked
    }));
    
    if (isLiked) {
      dispatch(unlikeComment({ commentId, token }));
    } else {
      dispatch(likeComment({ commentId, token }));
    }
  }, [dispatch, token]);

  const SORT_OPTIONS = useMemo(() => SORT_OPTION_META.map(opt => ({
    ...opt,
    label: t(`stockers.posts.sortOptions.${opt.id}.label`),
    description: t(`stockers.posts.sortOptions.${opt.id}.description`)
  })), [t]);

  const TIME_FILTERS = useMemo(() => TIME_FILTER_IDS.map(id => ({
    id,
    label: t(`stockers.posts.timeFilters.${id}`)
  })), [t]);

  // Get current sort option and time filter for display
  const selectedSortOption = SORT_OPTIONS.find(option => option.id === localSortBy);
  const selectedTimeFilter = TIME_FILTERS.find(filter => filter.id === localTimeFilter);

  // Create trending score map for display
  const trendingScoreMap = useCallback(() => {
    const map = new Map();
    trendingItems.forEach(item => {
      map.set(item.id, item.trend_score || 0);
    });
    return map;
  }, [trendingItems]);

  // Loading state
  if ((loading || filterLoading) && posts.length === 0) {
    return (
      <div>
        {Array.from({ length: 3 }).map((_, i) => (
          <PostCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        {t('stockers.posts.errorLoadingPostsPrefix')} {typeof error === 'object' ? JSON.stringify(error) : error}
      </div>
    );
  }

  // No posts state
  if (!posts || posts.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500 text-lg">{t('stockers.posts.noPostsAvailable')}</div>
        {isFiltered && (
          <button
            onClick={() => {
              dispatch(setPostFilters({ sort_by: 'latest', period: 'all' }));
              dispatch(resetPostsForNewQuery());
              dispatch(fetchPostsWithPictures());
            }}
            className="mt-4 px-4 py-2 bg-violet-700 text-white rounded-lg hover:bg-violet-600 transition-colors"
          >
            {t('stockers.posts.clearFilters')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sorting Controls */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-4 border border-gray-700/50 shadow-xl">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex items-center gap-4 flex-wrap">
            {/* Sort Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                disabled={filterLoading}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {filterLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  selectedSortOption && (
                    <selectedSortOption.icon 
                      className={`w-4 h-4 ${selectedSortOption.iconRotation || ''}`} 
                    />
                  )
                )}
                <span className="font-medium">{selectedSortOption?.label || t('stockers.posts.sortFallback')}</span>
                <ArrowUpDown className="w-4 h-4" />
              </button>

              {showSortDropdown && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-gray-800 border border-gray-600 rounded-xl shadow-2xl z-50 overflow-hidden">
                  <div className="p-2 bg-gradient-to-r from-purple-600/20 to-blue-600/20 border-b border-gray-600">
                    <h3 className="text-sm font-semibold text-gray-200">{t('stockers.posts.sortPostsBy')}</h3>
                  </div>
                  {SORT_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => handleSortChange(option.id)}
                      disabled={filterLoading}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-700/50 transition-colors duration-150 border-b border-gray-700/30 last:border-b-0 disabled:opacity-50 disabled:cursor-not-allowed ${
                        localSortBy === option.id 
                          ? 'bg-purple-600/20 text-purple-300 border-l-4 border-l-purple-500' 
                          : 'text-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <option.icon className={`w-4 h-4 ${option.iconRotation || ''} ${
                          localSortBy === option.id ? 'text-purple-400' : 'text-gray-500'
                        }`} />
                        <div>
                          <div className="font-medium">{option.label}</div>
                          <div className="text-xs text-gray-500 mt-1">{option.description}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Time Filter Dropdown */}
            <div className="relative" ref={timeFilterRef}>
              <button
                onClick={() => setShowTimeFilter(!showTimeFilter)}
                disabled={filterLoading}
                className="flex items-center gap-2 px-4 py-2 bg-gray-700/50 text-gray-300 rounded-lg hover:bg-gray-600/50 transition-all duration-200 border border-gray-600/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Filter className="w-4 h-4" />
                <span>{selectedTimeFilter?.label || t('stockers.posts.timeFilters.all')}</span>
                <ArrowUpDown className="w-4 h-4" />
              </button>

              {showTimeFilter && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-gray-800 border border-gray-600 rounded-xl shadow-2xl z-50 overflow-hidden">
                  <div className="p-2 bg-gradient-to-r from-gray-700/50 to-gray-600/50 border-b border-gray-600">
                    <h3 className="text-sm font-semibold text-gray-200">{t('stockers.posts.timePeriod')}</h3>
                  </div>
                  {TIME_FILTERS.map((filter) => (
                    <button
                      key={filter.id}
                      onClick={() => handleTimeFilterChange(filter.id)}
                      disabled={filterLoading}
                      className={`w-full text-left px-4 py-2 hover:bg-gray-700/50 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed ${
                        localTimeFilter === filter.id 
                          ? 'bg-blue-600/20 text-blue-300 border-l-4 border-l-blue-500' 
                          : 'text-gray-300'
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Results Info */}
          <div className="flex items-center gap-4">
            {(filterLoading) && (
              <div className="flex items-center gap-2 text-blue-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">{t('stockers.posts.updating')}</span>
              </div>
            )}
            <div className="text-sm text-gray-400">
              {t('stockers.posts.showingPosts', { count: posts.length })}
              {localTimeFilter !== 'all' && (
                <span className="ml-2 px-2 py-1 bg-blue-600/20 text-blue-400 rounded-full text-xs">
                  {selectedTimeFilter?.label}
                </span>
              )}
              {isFiltered && (
                <span className="ml-2 px-2 py-1 bg-purple-600/20 text-purple-400 rounded-full text-xs">
                  {t('stockers.posts.filtered')}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Posts */}
      <div className="space-y-6">
        {posts.map((post) => {
          const isLiked = optimisticLikes[post.id] !== undefined ? 
                           optimisticLikes[post.id] : 
                           post.is_liked;
          
          const hasBullVote = optimisticBullBear[post.id]?.bull !== undefined ?
                              optimisticBullBear[post.id].bull :
                              post.has_bull_vote;
                              
          const hasBearVote = optimisticBullBear[post.id]?.bear !== undefined ?
                              optimisticBullBear[post.id].bear :
                              post.has_bear_vote;
          
          const mergedComments = [
            ...(post.comments || []).filter(comment => 
              !(optimisticComments[post.id] || []).some(oc => oc.id === comment.id)
            ),
            ...(optimisticComments[post.id] || [])
          ];
          
          const commentsWithOptimisticLikes = mergedComments.map(comment => {
            const commentIsLiked = optimisticLikes[`comment-${comment.id}`] !== undefined ?
                                  optimisticLikes[`comment-${comment.id}`] :
                                  comment.is_liked;
            return {
              ...comment,
              is_liked: commentIsLiked,
              like_count: commentIsLiked && !comment.is_liked ? 
                          comment.like_count + 1 : 
                          (!commentIsLiked && comment.is_liked ? 
                           Math.max(0, comment.like_count - 1) : 
                           comment.like_count)
            };
          });
          
          const optimisticLikeCount = isLiked && !post.is_liked ? 
                                     post.likes + 1 : 
                                     (!isLiked && post.is_liked ? 
                                      Math.max(0, post.likes - 1) : 
                                      post.likes);
          
          const optimisticBullCount = hasBullVote && !post.has_bull_vote ?
                                     post.bull_count + 1 :
                                     (!hasBullVote && post.has_bull_vote ?
                                      Math.max(0, post.bull_count - 1) :
                                      post.bull_count);
                                      
          const optimisticBearCount = hasBearVote && !post.has_bear_vote ?
                                     post.bear_count + 1 :
                                     (!hasBearVote && post.has_bear_vote ?
                                      Math.max(0, post.bear_count - 1) :
                                      post.bear_count);
          
          // Add trending score to post if available
          const trendingMap = trendingScoreMap();
          const postWithOptimisticData = {
            ...post,
            comments: commentsWithOptimisticLikes,
            likes: optimisticLikeCount,
            bull_count: optimisticBullCount,
            bear_count: optimisticBearCount,
            is_liked: isLiked,
            has_bull_vote: hasBullVote,
            has_bear_vote: hasBearVote,
            trending_score: trendingMap.get(post.id) || 0
          };
          
          return (
            <Post
              key={post.id}
              post={postWithOptimisticData}
              onViewed={handlePostViewed}
              isCommentBoxOpen={openCommentBoxPostId === post.id}
              toggleCommentBox={toggleCommentBox}
              onLikeToggle={handleLikeToggle}
              onBullVote={handleBullVote}
              onBearVote={handleBearVote}
              onCommentSubmit={handleCommentSubmit}
              onCommentLikeToggle={(commentId, isLiked) => handleCommentLikeToggle(commentId, post.id, isLiked)}
            />
          );
        })}
      </div>

      {/* Loading more indicator */}
      {(loading || filterLoading) && posts.length > 0 && (
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center gap-3 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>{t('stockers.posts.loadingMorePosts')}</span>
          </div>
        </div>
      )}

      {/* End of posts indicator */}
      {!hasMore && posts.length > 0 && !loading && !filterLoading && (
        <div className="text-center py-8">
          <div className="text-gray-500 text-sm">
            {isFiltered ? t('stockers.posts.noMorePostsFiltered') : t('stockers.posts.reachedTheEnd')}
          </div>
          {isFiltered && (
            <button
              onClick={() => {
                dispatch(setPostFilters({ sort_by: 'latest', period: 'all' }));
                dispatch(resetPostsForNewQuery());
                dispatch(fetchPostsWithPictures());
              }}
              className="mt-4 px-4 py-2 text-sm bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
            >
              {t('stockers.posts.clearFilters')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Posts;